/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Handle violations", description = "Understand the types of contract violations and configure how Hightouch enforces them.", lastUpdatedDate = new Date(1785283200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Handle violations",
        description: "Understand the types of contract violations and configure how Hightouch enforces them.",
        lastUpdatedDate: "Wed Jul 29 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Violation enforcement",
          "depth": 1,
          "id": "violation-enforcement"
        }, {
          "value": "Diagnose why an event was blocked or flagged",
          "depth": 0,
          "id": "diagnose-why-an-event-was-blocked-or-flagged"
        }, {
          "value": "Event name doesn't match the contract",
          "depth": 1,
          "id": "event-name-doesnt-match-the-contract"
        }, {
          "value": "Fields are in the wrong place",
          "depth": 1,
          "id": "fields-are-in-the-wrong-place"
        }, {
          "value": "Field type doesn't match",
          "depth": 1,
          "id": "field-type-doesnt-match"
        }, {
          "value": "Every event is blocked or the schema appears empty",
          "depth": 1,
          "id": "every-event-is-blocked-or-the-schema-appears-empty"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      img: "img",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "violation"
        }), " occurs when an event sent through an event source does not satisfy the contract applied to that source."]
      }), "\n", _jsx(_components.p, {
        children: "Violations help you identify incomplete, inconsistent, or unexpected event data before it impacts downstream tools."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Domains match events exactly. The event name, field names, and field types in\nan incoming event must match the schema as written, including capitalization\nand spaces. A domain schema is a precise rule for the event data, not a loose\nset of naming guidelines."
        })
      }), "\n", _jsx(_components.p, {
        children: "Events can cause three types of violations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "undeclared event"
          }), " violation occurs when a specific event is not\nspecified in any domain applied to the source", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["e.g. ", _jsx(_components.code, {
                children: "Button Clicked"
              }), " event is sent to Hightouch, but it is not in the\nE-commerce Events domain"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "undeclared field"
          }), " violation occurs when a field in an event is not\nspecified in the validation schema", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["e.g. ", _jsx(_components.code, {
                children: "user_name"
              }), " field is sent to Hightouch in the ", _jsx(_components.code, {
                children: "Order Completed"
              }), "\npayload, but it is not specified in the validation schema"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "invalid field"
          }), " violation occurs when a field does not pass the schema\nvalidation defined by JSON Schema rules", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["e.g. ", _jsx(_components.code, {
                children: "userId"
              }), " is sent as a ", _jsx(_components.code, {
                children: "string"
              }), " when the schema defines it as a ", _jsx(_components.code, {
                children: "number"
              })]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["All violations are written to a separate ", _jsx(_components.code, {
          children: "event_violations"
        }), " table in your\ndestination, regardless of how violation enforcement is configured."]
      }), "\n", _jsx(_components.p, {
        children: "This table has the schema:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "payload"
          }), ": JSONB"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "event_name"
          }), ": string (derived from payload)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "event_type"
          }), ": string (derived from payload)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "schema_version"
          }), ": string (derived from payload)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "has_undeclared_schema"
          }), ": boolean"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "has_undeclared_fields"
          }), ": boolean"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "has_schema_violation"
          }), ": boolean"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "violations"
          }), ": string[]"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "violation-enforcement",
        children: _jsx(_components.strong, {
          children: "Violation enforcement"
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to configure how to handle violations at the source and\nevent level. Depending on the cleanliness of your current event tracking, you may want\nto allow violations to be written to your main event tables."
      }), "\n", _jsx(_components.p, {
        children: "This table shows how Hightouch can handle each violation type. The\nbolded actions represent the default behavior."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "center",
              children: "Invalid fields"
            }), _jsx(_components.th, {
              align: "center",
              children: "Undeclared field"
            }), _jsx(_components.th, {
              align: "center",
              children: "Undeclared event"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "center",
              children: _jsx(_components.strong, {
                children: "Allow"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: _jsx(_components.strong, {
                children: "Allow"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: _jsx(_components.strong, {
                children: "Allow"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "center",
              children: "Block"
            }), _jsx(_components.td, {
              align: "center",
              children: "Block"
            }), _jsx(_components.td, {
              align: "center",
              children: "Block"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "center"
            }), _jsx(_components.td, {
              align: "center",
              children: "Filter"
            }), _jsx(_components.td, {
              align: "center"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Allow"
        }), " writes the event and records the violation. ", _jsx(_components.strong, {
          children: "Block"
        }), " rejects the event. ", _jsx(_components.strong, {
          children: "Filter"
        }), " drops only the undeclared fields and writes the rest of the event."]
      }), "\n", _jsx(_components.p, {
        children: "Configure how to handle undeclared events on the source's detail page."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/contracts/undeclared-events-button.png",
          alt: "Undeclared events button"
        })
      }), "\n", _jsx(_components.p, {
        children: "Schema violations and undeclared fields can be configured at an event-level on the event schema page."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/contracts/violation-enforcement-event.png",
          alt: "Violation enforcement screenshot"
        })
      }), "\n", _jsx(_components.h2, {
        id: "diagnose-why-an-event-was-blocked-or-flagged",
        children: "Diagnose why an event was blocked or flagged"
      }), "\n", _jsx(_components.p, {
        children: "When an event doesn't arrive the way you expect, it usually maps to one of the three violation types above. Most of the time the cause is a small mismatch between what the SDK sends and what the contract declares, not a missing contract."
      }), "\n", _jsxs(_components.p, {
        children: ["Start with the ", _jsx(_components.code, {
          children: "event_violations"
        }), " table or the ", _jsx(_components.a, {
          href: "/events/sources/overview#debugger",
          children: "debugger"
        }), " to see which violation fired, then check the matching cause below."]
      }), "\n", _jsx(_components.h3, {
        id: "event-name-doesnt-match-the-contract",
        children: "Event name doesn't match the contract"
      }), "\n", _jsxs(_components.p, {
        children: ["A small difference between the event name in your tracking code and the event name in the contract registers as an ", _jsx(_components.strong, {
          children: "undeclared event"
        }), ". The match is exact, so these all count as different events:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Order Completed"
          }), " and ", _jsx(_components.code, {
            children: "order completed"
          }), " (capitalization)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Order Completed"
          }), " and ", _jsx(_components.code, {
            children: "Order Completed"
          }), " (extra space)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Order Completed "
          }), " and ", _jsx(_components.code, {
            children: "Order Completed"
          }), " (trailing space)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Confirm the event name in your code matches the contract character for character. If you block undeclared events, a mismatch blocks the event."
      }), "\n", _jsx(_components.h3, {
        id: "fields-are-in-the-wrong-place",
        children: "Fields are in the wrong place"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch validates a field by where it sits in the event, not only by its name. A field that the schema expects under ", _jsx(_components.code, {
          children: "context"
        }), " but the SDK sends under ", _jsx(_components.code, {
          children: "properties"
        }), " registers as an ", _jsx(_components.strong, {
          children: "undeclared field"
        }), " in ", _jsx(_components.code, {
          children: "properties"
        }), ", and as a missing field in ", _jsx(_components.code, {
          children: "context"
        }), ". Page and screen metadata are common cases, since some of it belongs in ", _jsx(_components.code, {
          children: "context"
        }), " rather than ", _jsx(_components.code, {
          children: "properties"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Check that each field is in the section the schema declares."
      }), "\n", _jsx(_components.h3, {
        id: "field-type-doesnt-match",
        children: "Field type doesn't match"
      }), "\n", _jsxs(_components.p, {
        children: ["A field sent as the wrong type registers as an ", _jsx(_components.strong, {
          children: "invalid field"
        }), " violation. A common case is an identifier sent as a string when the schema defines it as a number, or the reverse. Align the type in your tracking code with the type in the schema."]
      }), "\n", _jsx(_components.h3, {
        id: "every-event-is-blocked-or-the-schema-appears-empty",
        children: "Every event is blocked or the schema appears empty"
      }), "\n", _jsx(_components.p, {
        children: "If a whole source is blocked, check the domains applied to the source before the events. An empty domain combined with settings that block undeclared events and fields blocks every incoming event, because nothing matches a domain that declares nothing. Confirm the domain has its events and fields defined, then confirm enforcement is set the way you intend."
      }), "\n", _jsxs(_components.p, {
        children: ["When undeclared events are blocked and no matching schema exists, the raw violation recorded for the event is ", _jsx(_components.code, {
          children: "Schema not found"
        }), ". You'll see this text in the ", _jsx(_components.code, {
          children: "event_violations"
        }), " table and in the debugger's event detail. The debugger's blocked-state badge shows a different message in its tooltip — ", _jsx(_components.code, {
          children: "Undeclared events are blocked in your contracts settings"
        }), " — which explains the enforcement decision rather than the raw violation."]
      })]
    });
  }
}
export default MDXContent;
